import {
  getContractShareContract,
  getContractShareByContract,
  getContractShareByRole,
  getContractShareUser,
  bulkContractShare,
  removeContractShare,
  removeContractShare2 as removeContractRoleShare,
  updateContractSharePermissions,
  updateContractSharePermissions2 as updateContractRoleSharePermissions,
  searchSharesByContract,
  searchSharesByContract2,
  type ApplicationMessage,
  type ContractUserShareInDb,
  type ContractUserShareInDbContract,
  type ContractRoleShareInDb,
  type ContractRoleShareInDbContract,
} from '@/api';
import { toAccessPermission } from '@/utils/accessPermission';

/**
 * Extract error message from API errors
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export interface ContractShareSearchParams {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

export const ContractShareService = {
  /**
   * Get all user shares for a contract
   */
  async getUserSharesByContract(contractUid: string): Promise<ContractUserShareInDb[]> {
    try {
      const response = await getContractShareContract({ path: { contract_uid: contractUid } });
      if (!response.data) {
        throw new Error('Failed to fetch contract user shares');
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to load shared users. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get all role shares for a contract
   */
  async getRoleSharesByContract(contractUid: string): Promise<ContractRoleShareInDb[]> {
    try {
      const response = await getContractShareByContract({ path: { contract_uid: contractUid } });
      if (!response.data) {
        throw new Error('Failed to fetch contract role shares');
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to load shared roles. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get all contract shares for a role (contracts shared with this role)
   */
  async getContractSharesByRole(roleUid: string): Promise<ContractRoleShareInDb[]> {
    try {
      const response = await getContractShareByRole({ path: { role_uid: roleUid } });
      if (!response.data) {
        throw new Error('Failed to fetch contracts shared with this role');
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          'Failed to load contracts shared with this role. Please try again.'
        ),
        { cause: error }
      );
    }
  },

  /**
   * Get all contract shares for a user (contracts shared with this user)
   */
  async getContractSharesByUser(userUid: string): Promise<ContractUserShareInDb[]> {
    try {
      const response = await getContractShareUser({ path: { user_uid: userUid } });
      if (!response.data) {
        throw new Error('Failed to fetch contracts shared with this user');
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          'Failed to load contracts shared with this user. Please try again.'
        ),
        { cause: error }
      );
    }
  },

  /**
   * Search user shares for a contract (paginated)
   */
  async searchUserSharesByContract(
    contractUid: string,
    params: ContractShareSearchParams = {}
  ): Promise<{ results: ContractUserShareInDbContract[]; result_count: number }> {
    try {
      const pageSize = params.pageSize ?? 20;
      const page = params.page ?? 1;
      const body = {
        text: params.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: params.sortKey ?? 'created_at',
        sort_direction: params.sortDirection ?? 'desc',
        similarity_threshold: 0.5,
      };
      const response = await searchSharesByContract({
        path: { contract_uid: contractUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to search contract user shares');
      }
      return {
        results: response.data.results,
        result_count: response.data.result_count,
      };
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to search shared users. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Search role shares for a contract (paginated)
   */
  async searchRoleSharesByContract(
    contractUid: string,
    params: ContractShareSearchParams = {}
  ): Promise<{ results: ContractRoleShareInDbContract[]; result_count: number }> {
    try {
      const pageSize = params.pageSize ?? 20;
      const page = params.page ?? 1;
      const body = {
        text: params.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: params.sortKey ?? 'created_at',
        sort_direction: params.sortDirection ?? 'desc',
        similarity_threshold: 0.5,
      };
      const response = await searchSharesByContract2({
        path: { contract_uid: contractUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to search contract role shares');
      }
      return {
        results: response.data.results,
        result_count: response.data.result_count,
      };
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to search shared roles. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Share contracts with users and/or roles (create or update, cross product).
   * Permissions is a bitmask (e.g. READ|WRITE = 3) applied to every created/updated share.
   */
  async bulkShareContracts(params: {
    contractUids: string[];
    userUids?: string[];
    roleUids?: string[];
    permissions: number;
  }): Promise<ApplicationMessage> {
    try {
      const response = await bulkContractShare({
        body: {
          contract_uids: params.contractUids,
          user_uids: params.userUids ?? [],
          role_uids: params.roleUids ?? [],
          permissions: toAccessPermission(params.permissions),
        },
      });
      if (!response.data) {
        throw new Error('Failed to share contracts');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to share. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Remove a user share
   */
  async removeUserShare(contractUid: string, userUid: string): Promise<void> {
    try {
      await removeContractShare({
        path: { contract_uid: contractUid, user_uid: userUid },
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to remove user share. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Remove a role share
   */
  async removeRoleShare(contractUid: string, roleUid: string): Promise<void> {
    try {
      await removeContractRoleShare({
        path: { contract_uid: contractUid, role_uid: roleUid },
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to remove role share. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Update permissions for a user share. Permissions is a bitmask (e.g. READ|WRITE = 3).
   */
  async updateUserSharePermissions(
    contractUid: string,
    userUid: string,
    permissions: number
  ): Promise<ContractUserShareInDb> {
    try {
      const response = await updateContractSharePermissions({
        path: { contract_uid: contractUid, user_uid: userUid },
        body: { permissions: toAccessPermission(permissions) },
      });
      if (!response.data) {
        throw new Error('Failed to update user share permissions');
      }
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update permissions. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Update permissions for a role share. Permissions is a bitmask (e.g. READ|WRITE = 3).
   */
  async updateRoleSharePermissions(
    contractUid: string,
    roleUid: string,
    permissions: number
  ): Promise<ContractRoleShareInDb> {
    try {
      const response = await updateContractRoleSharePermissions({
        path: { contract_uid: contractUid, role_uid: roleUid },
        body: { permissions: toAccessPermission(permissions) },
      });
      if (!response.data) {
        throw new Error('Failed to update role share permissions');
      }
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update permissions. Please try again.'),
        { cause: error }
      );
    }
  },
};
